/**
 * The per-account price book: the captured rows, and the strip that edits them
 * (T651 M2 scaffolding, FILLED BY M5).
 *
 * ⭐ WHAT THIS SCREEN IS FOR, IN ONE LINE: what a home pays to go somewhere is
 * DATA, not a deploy. Owen's two paper sheets became 45 figures here, and the
 * forty-sixth is typed in rather than released.
 *
 * ⛔ ROUTING IS THE PARENT'S AND STAYS THERE. Admin.tsx owns the view union and
 * the loaders; AccountBook.tsx owns the tabs, the truncation notice and this
 * file's prop contract. Nothing here reopens either — M2 froze both.
 *
 * ⛔ A PRICE SHOWN HERE IS NOT WHAT AN INVOICE READS. This table is edited in
 * place; the per-booking snapshot in `quote_versions` is what an invoice line
 * reads, so editing a price today cannot reprice a journey that already
 * travelled. That is the invoice invariant, recorded in-schema on
 * `account_price_audit`.
 *
 * ⛔ AND THIS SCREEN NEVER DECIDES A PRICE FOR A JOURNEY. It holds the whole
 * book because DISPLAYING it is its entire job, which is why
 * test/core/account-prices-resolve.spec.ts allow-lists this one file by name.
 * The allowance is to RENDER every row, not to search them: answering "what does
 * this home pay to go there?" is D-RESOLVE's, through the single-pair arm of the
 * route. Nothing below calls `.find` on `prices`.
 * ⛔ AND NO PIN ENFORCES THAT SECOND SENTENCE HERE — THIS FILE IS THE ONE PLACE
 * THE RULE IS UNCHECKED (T1683 M8 ux-review; the earlier wording claimed "the
 * pin reds if it does" and was MEASURED FALSE — a `prices.find(...)` added
 * inside `groupByAccount` leaves the sweep green at 19/19). `DISPLAY_CONSUMERS`
 * exempts this file BY NAME, and a file-scoped exemption is total: the sweep
 * protects every other client file from the bypass and cannot see this one.
 * Enforcement here is by REVIEW, which is a weaker guarantee and is said so
 * rather than implied. ⚠️ An allow-list that exempts a file makes it the place
 * the rule is most likely to be broken. Narrow it — exempt the RENDER rather
 * than the file — when anything in here needs to search the book for a reason
 * other than laying it out; today the consumers are `groupByAccount`, a
 * one-pass gather, and (board 2705) `heldDestinationIds`, which reads the
 * DESTINATION IDS of one home's already-gathered block so the Add form's picker
 * can leave out the places that home already has. ⛔ IT READS NO FIGURE and
 * returns a set of ids, so it cannot answer "what does this home pay to go
 * there?" - it answers "is there a row at all", which is a picker question, the
 * same split the server's figure-free picker read rests on.
 * ⚠️ A TRUNCATED BOOK can leave a held destination out of its block, so the
 * picker may still offer it; the server's 409 stays authoritative.
 *
 * ⛔ ZERO IS A PRICE AND IS NOT "NOT PRICED YET", and this is the second of the
 * two places that difference is pinned (the first is the resolver's `== null`).
 * A falsy check here would render a free run as unpriced and send the operator
 * to type a charge nobody agreed.
 *
 * ⚠️ S28 (a) — THE CLICK CHANNEL — IS LIVE ON THIS SCREEN FOR THE FIRST TIME,
 * AND THAT IS THE CHANGE M5 MAKES. Until this milestone the file was a
 * `<ul>/<li>/<span>` stub, so `closest(INTERACTIVE_SELECTOR)` returned null on
 * every click and `describeTarget` recorded `''` — the markers would have been
 * inert. The edit strip below puts REAL CONTROLS beside a real customer
 * organisation's name and a real healthcare facility's name, so every control
 * carries `data-fb-opaque`, read by feedback/clicks.ts. The blanket is
 * deliberate: a uniform rule survives edits better than one applied only to the
 * element somebody reasoned about.
 * ⚠️ Note where the names actually sit — in the `<span class="op-name">`
 * SIBLINGS of the buttons, which are not in INTERACTIVE_SELECTOR, so a click on
 * one resolves no control at all. If a row is ever made clickable as a whole,
 * THAT control is the one that needs the marker.
 * ⛔ S28 (b) — THE SCREENSHOT CHANNEL — IS A RECORDED RESIDUAL AND NOT A FIX,
 * RE-STATED HERE RATHER THAN SILENTLY INHERITED FROM M3 AND M4.
 * `data-fb-opaque` is honoured ONLY by the click recorder; the capture path
 * blanks the VALUE of cloned inputs and reproduces rendered TEXT verbatim, so
 * the names below DO reach the JPEG on a build with the widget enabled. What
 * bounds it: the widget is build-gated (a code property, not a deployment
 * guarantee). What would close it is extending the capture filter under
 * src/client/feedback, which is generated from the copy under site/ — the
 * barrier is this milestone's file-ownership boundary plus `sync:feedback:check`.
 * ⛔ AND THE CAPTURE FILTER'S OWN MARKER ATTRIBUTE IS BARRED AS THE MITIGATION,
 * the same literal `AccountList.tsx` records, in its price variant: the names
 * AND the rate figures below reach the JPEG, `scripts/check-killswitch.mjs`
 * greps the built client, and the served sourcemap embeds these comments, so
 * even naming the literal fails the gate.
 * ⚠️ AND THE CONSENT ITEM WIDENS ONE MORE TIME, which is the part a
 * decision-maker needs. The open `PENDING - ALEX` ask for "an explicit nod that
 * kiosk screenshots may carry customer PII" was raised when the kiosk held
 * synthetic fixtures. M3 changed its subject to two named real customer
 * organisations; M4 added 29 named real healthcare facilities they travel to;
 * THIS milestone adds WHAT EACH HOME PAYS FOR EACH JOURNEY. A commercial rate
 * card, per named customer, is a different kind of fact again from either, and
 * it must not be closed against the old subject.
 */
import { useEffect, useRef, useState } from 'preact/hooks';
import { EmptyState, Table, TableScroll } from '@ecmb/ui';
import type { ApiAccount, ApiAccountPrice, ApiDestination } from '../../lib/actions.js';
import {
  createAccountPrice,
  setAccountPrice,
  removeAccountPrice,
  parseEuroToCents,
  type PriceResult,
} from '../../lib/price-actions.js';
// T1704 — the operator trigger. Its own module, per the house rule the sibling
// action files record; NOT an append to the frozen lib/actions.ts.
import { projectPortalDestinations } from '../../lib/portal-projection-actions.js';
// T1723 M3 (K3) - the DISPLAY figure's formatter. The house one, shared with the
// rate table and the quote, so no currency symbol is ever typed into this file.
import { euro } from '../../lib/format.js';
// ⛔ THE SAME HASH AND THE SAME KEY THE SERVER WRITES. Two local copies that
// agreed today would drift tomorrow, and the screen would then read "Prices have
// changed since this was last sent." for ever on a book nobody touched.
import {
  fingerprintDestinations,
  portalDestinationsSentKey,
} from '../../../shared/projection-fingerprint.js';
import { relativeAge } from '../../lib/format.js';
import { Banner } from '../../components/Banner.js';

export type PriceListProps = {
  prices: ApiAccountPrice[];
  /** The two key catalogues a price is written against. */
  accounts: ApiAccount[];
  destinations: ApiDestination[];
  /**
   * True when an empty `prices` might NOT mean "there are none" — the read
   * failed, or it has not resolved yet. Suppresses the empty-state sentence,
   * because AccountBook is saying the honest thing above instead.
   *
   * ⛔ ABSENT MEANS THE CALLER HAS **PROVEN** THIS LIST IS REALLY EMPTY. Any call
   * site rendering this list from a read that can fail must pass it. Optional
   * only so the shipped spec harness compiles unchanged; a second production
   * call site that forgets it silently reinstates the defect (T698), and `tsc`
   * will not say a word. AccountBook.tsx owns this contract.
   *
   * ⚠️ KEYED ON THE PRICES READ ALONE. The two catalogues above also feed this
   * screen's pickers, and a failure in EITHER makes this tab incomplete — but
   * that is the banner's business, not this sentence's.
   */
  emptinessUnknown?: boolean;
  /**
   * T1704 — `app_meta` as the screen last loaded it, the only place the kiosk
   * records that a projection was sent.
   *
   * ⛔ `null` MEANS THE SCREEN DOES NOT KNOW YET, and it is a THIRD state, not
   * an empty map. An empty map is a kiosk that has never sent anything — which
   * is a fact worth saying. `null` is Admin.tsx's meta read still loading or
   * failed, and asserting "Not sent to the portal yet." on the strength of that
   * is exactly the class of lie `emptinessUnknown` two fields up exists to
   * stop. The verdict is suppressed instead.
   */
  meta: Record<string, string> | null;
  /**
   * True when the price read came back CUT (AccountBook's `truncated.prices`).
   *
   * ⛔ IT SUPPRESSES THE VERDICT ENTIRELY, and that is not defensiveness. The
   * bounded read cuts across ALL accounts at once, ordered by account name then
   * destination name, so a cut hands alphabetically-late accounts a SHORT block
   * — and a fingerprint over a short block cannot match the one that was sent.
   * The screen would then tell Helen the prices had changed when nothing had,
   * under a banner already saying rows are missing.
   */
  pricesTruncated: boolean;
  /** The house invalidation (Admin.tsx passes `load`). */
  onChanged: () => void;
  /**
   * ⭐ board 2812 M4 (plan step 3): a home's Prices tab shows THIS account's prices only
   * (2705's list, its "Remove" / "Keep it" and its routes unchanged). The narrowing is
   * done HERE, in the one display consumer allowed to hold the book
   * (`test/core/account-prices-resolve.spec.ts`), never by a screen searching it.
   */
  onlyAccountId?: number;
};

/**
 * Cents to the figure the operator TYPES BACK, and to nothing else any more.
 *
 * ⭐ T1723 M3 (K3) SPLIT THE TWO JOBS THIS FUNCTION USED TO DO. It fed the
 * EDIT field at `startEdit` AND the row's display figure, and the display half
 * was the finding: `85.00` with no symbol, in `.op-login` (17px, 400, ink-600),
 * smaller and greyer than its own label. The display figure now renders through
 * `euro()` from `lib/format.js`, the ONE formatter (`shared/money-format.ts`)
 * the rate table, the quote and the customer's email all already share, so the
 * symbol enters the screen from a shared module rather than as a literal here.
 *
 * ⛔ AND THE EDIT FIELD KEEPS THIS ONE, which is the half that must not move:
 * `parseEuroToCents` reads that field back, and it parses a bare decimal.
 * `euro()` emits the symbol and en-IE GROUPING (`€1,250`), so filling the box
 * with it would hand the parser a string it refuses.
 *
 * ⛔ NO CURRENCY SYMBOL HERE: `scripts/check-copy.sh` bans a hardcoded one in
 * shipped copy, and the field's own label carries the unit instead.
 */
const asEuro = (cents: number): string => (cents / 100).toFixed(2);

/** ⭐ board 2812 M5 (fix round 1, W5): the removal's no-reason refusal, said beside its
 *  field (the words are unchanged; only where they sit moved). One confirm is open at a
 *  time, so one id serves every row. */
export const PRICE_REMOVE_WHY_WORDS = 'Say why it is coming off the list, in a few words. It is kept with your name against this price.';
const PRICE_REMOVE_WHY_ERROR_ID = 'price-remove-why-error';

/** One account's block of the book: the account, and its priced destinations. */
type AccountBlock = { accountId: number; accountName: string; rows: ApiAccountPrice[] };

/**
 * The book, as a book: the rows already in hand, gathered under the account
 * each one belongs to (board 968's P1 ask, T1683 M8).
 *
 * ⭐ NO NEW READ, AND THAT IS THE WHOLE SHAPE OF THIS CHANGE. The list route
 * already joins `a.name AS account_name` onto every row, so the account a price
 * belongs to arrives with the price. Fetching accounts again to label a heading
 * would add a second source for a fact this screen is already holding.
 *
 * ⛔ GROUPED ON THE ID, NEVER ON THE NAME. Two accounts may legitimately carry
 * the same name (the roster screen has the same problem and answers it with the
 * town) and they are two different customers with two different books. Keying on
 * `accountId` cannot merge them; keying on the name silently would, and the
 * thing it would merge is money.
 *
 * ⛔⛔ AND NEVER GROUPED ON `accounts.kind`, WHICH IS THE MISTAKE THIS SCREEN IS
 * MOST LIKELY TO INVITE — the catalogue is right there in the props, and a book
 * filed under "Nursing homes / Schools / Societies / Something else" reads like
 * the tidier design. It is not, for a reason the column's own comment states:
 * NULL and 'other' are DIFFERENT ANSWERS. NULL means nobody has classified this
 * account yet; 'other' means an operator looked and it is none of the three,
 * which is a decision. `kind` is NULL on both live accounts, so a kind-grouped
 * book would file every real customer under a heading asserting a classification
 * that was never made. An account appears under ITS OWN NAME, always.
 * ⛔ Nor is `kind` ever compared to a service id. `persist-quote.ts` holds
 * NURSING_SERVICE_ID = 'nursing-home', spelled identically to the kind token on
 * purpose and declared independently with no import either way: one says what
 * the ORGANISATION is, the other says which SERVICE a journey was booked under.
 * A school may book the nursing service, and a home may book a day tour.
 *
 * ⚠️ A GATHER, NOT A SEARCH. It walks the rows once and appends; nothing here
 * asks the book "what does this home pay to go there?" — that question is
 * D-RESOLVE's, through the single-pair arm of the route.
 * ⛔ AND THAT IS TRUE BY CONSTRUCTION, NOT BY MEASUREMENT: the allow-list sweep
 * in test/core/account-prices-resolve.spec.ts exempts this whole FILE, so it
 * would have nothing to say here either way. See the header — the guarantee is
 * review, not a gate.
 *
 * ⚠️ INSERTION-ORDERED, so the server's `ORDER BY a.name, d.name` survives the
 * gather and the book still reads alphabetically. Rows arriving interleaved
 * (a caller that sorted differently) still group correctly — the order of the
 * BLOCKS is first-seen, and no block is ever split in two.
 */
const groupByAccount = (prices: ApiAccountPrice[]): AccountBlock[] => {
  const blocks = new Map<number, AccountBlock>();
  for (const p of prices) {
    const block = blocks.get(p.accountId);
    if (block == null) {
      blocks.set(p.accountId, { accountId: p.accountId, accountName: p.accountName, rows: [p] });
    } else {
      block.rows.push(p);
    }
  }
  return [...blocks.values()];
};

/**
 * ⭐ board 2705 — the destinations ONE home already has a price for, as ids, so
 * the Add form's picker can leave them out ("too many options": the picker
 * offered every place, and choosing one the home already had was a 409 on save).
 *
 * ⛔ IDS ONLY, READ OFF THE BLOCK THIS SCREEN ALREADY GATHERED. No figure is
 * read, so nothing here can decide what a home pays (see the header's
 * allow-list note). `''` (no home chosen) holds nothing.
 */
const heldDestinationIds = (blocks: AccountBlock[], accountId: string): Set<number> => {
  const block = blocks.find((b) => String(b.accountId) === accountId);
  return new Set(block == null ? [] : block.rows.map((r) => r.destinationId));
};

/**
 * ⭐ T1704 — WHAT THE SCREEN SAYS ABOUT ONE BLOCK'S PROJECTION, or null for
 * "say nothing at all".
 *
 * ⛔ IT IS A KIOSK-SIDE MEMORY OF AN ACT, NEVER AN OBSERVATION OF THE PORTAL.
 * The record is written here when a send succeeds; a portal restore, reset or
 * redeploy leaves this screen confidently claiming freshness. Closing that needs
 * a read back across the seam, which is deliberately out of scope — stated
 * rather than hidden, because the line reads like an observation and is not one.
 *
 * ⛔ AND IT IS BUILT FROM THE SET, NOT FROM A TIMESTAMP ALONE. No price crosses
 * the seam (the source read selects no figure), so editing a price CANNOT stale
 * the portal and an "edited since" clock would cry wolf on every price change.
 * Only the SET of destinations can go stale, so only the set is compared.
 */
const projectionVerdict = (
  meta: Record<string, string> | null,
  pricesTruncated: boolean,
  block: AccountBlock,
): string | null => {
  if (meta === null || pricesTruncated) return null;
  const raw = meta[portalDestinationsSentKey(block.accountId)];
  if (raw == null) return 'Not sent to the portal yet.';

  let record: { at?: unknown; fingerprint?: unknown };
  try {
    record = JSON.parse(raw) as { at?: unknown; fingerprint?: unknown };
  } catch {
    // ⛔ A RECORD NOBODY CAN READ IS NOT EVIDENCE OF A SEND. Degrading to the
    // never-sent line is the safe direction: it invites a re-send, which is
    // idempotent, rather than vouching for a projection nothing can confirm.
    return 'Not sent to the portal yet.';
  }
  if (typeof record.fingerprint !== 'string') return 'Not sent to the portal yet.';

  const current = fingerprintDestinations(
    block.rows.map((r) => ({ destinationId: r.destinationId, name: r.destinationName })),
  );
  if (record.fingerprint !== current) return 'Prices have changed since this was last sent.';

  // `relativeAge` answers '' on junk input or a future timestamp (clock skew),
  // so the age is appended only when it is really an age. Never "Sent to the
  // portal ." with a hole where the date should be.
  const age = typeof record.at === 'string' ? relativeAge(record.at) : '';
  return age === '' ? 'Sent to the portal.' : `Sent to the portal ${age}.`;
};

export function PriceList({
  prices: allPrices,
  accounts,
  destinations,
  emptinessUnknown = false,
  meta,
  pricesTruncated,
  onChanged,
  onlyAccountId,
}: PriceListProps) {
  const prices = onlyAccountId === undefined ? allPrices : allPrices.filter((p) => p.accountId === onlyAccountId);
  /**
   * `at` is WHERE the message renders: `'form'` for the add/change form, or the
   * account id of the block whose "Send prices to the portal" was pressed.
   *
   * ⛔ BOARD 2810 (Helen, 2026-10-06): the message used to render once, ABOVE
   * the whole book, and the effect below focuses it. Focus scrolls, so a Save
   * pressed at the bottom of the book threw her back to the top (measured on a
   * seat: `.content` scrollTop 3134 -> 0). `preventScroll` alone would leave the
   * message screens above her and the Save would look silent, the defect the
   * focus note further down records. So the message renders where the action
   * happened and focus stays the screen-reader cue; the browser now scrolls a
   * short way, not the length of the book.
   */
  const [notice, setNotice] = useState<
    { kind: 'ok' | 'error'; text: string; at: 'form' | number } | null
  >(null);
  /** ⭐ board 2812 M5 (fix round 1, W5): the removal was pressed with no reason. The
   *  refusal is said BESIDE the reason's field, inside the row's confirm, never in the
   *  block's banner slot under "Send prices to the portal" (off the screen at 1024x700). */
  const [whyRefused, setWhyRefused] = useState(false);
  /** null = the form is adding; a number = the form is changing that row. */
  const [editing, setEditing] = useState<number | null>(null);
  /** ⭐ board 2812 M4: on a home's page the add form starts on (and returns to) that home. */
  const homeDefault = onlyAccountId === undefined ? '' : String(onlyAccountId);
  const [accountId, setAccountId] = useState(homeDefault);
  const [destinationId, setDestinationId] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const noticeRef = useRef<HTMLDivElement>(null);
  const amountRef = useRef<HTMLInputElement>(null);
  const homeRef = useRef<HTMLSelectElement>(null);
  /** Why focus is about to move. ⛔ NOT a boolean "should focus": the three
   *  reasons want three different targets, and a single flag is how the
   *  post-save case ends up stealing focus from the status message. */
  const pending = useRef<'edit' | 'cancel' | null>(null);
  /**
   * The amount the last ATTEMPT submitted, or null if none has been tried since
   * the target last changed.
   *
   * ⛔ BOARD 712 BOUND THE REASON TO THE TARGET AND THAT WAS NOT THE WHOLE
   * SUBJECT (T651 M5 security review, re-run). An audit row records
   * `old -> new, because <note>` — so the AMOUNT is part of what the reason is
   * about, not just the pair. In EDIT mode both selects are disabled, which
   * makes the amount the ONLY editable field that changes what gets journalled.
   * The flow the board-710 copy actively invites: type "Owen agreed 125 with
   * Mary", save, lose the race, be told to look and try again — then type 128
   * and save, filing a PERMANENT row reading `130 -> 128, because Owen agreed
   * 125 with Mary`. The old reset-on-every-outcome made that impossible; 712
   * removed it and opened this.
   *
   * ⚠️ COMPARED AGAINST THE SUBMITTED VALUE, NOT CLEARED ON EVERY KEYSTROKE.
   * Fixing a typo and arriving back at the same figure leaves the reason
   * truthful, so it survives; changing the figure does not.
   */
  const submitted = useRef<string | null>(null);
  // The double-submit latch, and it cannot be `busy`: `if (busy) return` reads a
  // value fixed at RENDER time and setBusy does not re-render before the next
  // click in the same task, so two fast taps both see false. A ref is written
  // and read synchronously. (Operators.tsx measured this; the shape is copied.)
  const inFlight = useRef(false);
  /**
   * ⭐ board 2705 — WHICH ROW IS ASKING "TAKE THIS OFF THE HOME'S LIST?", by
   * `accountPriceId`; null = none. DestinationList.tsx's confirm swap (K2): the
   * row's action cell REPLACES itself with a reason, a Yes and a Keep it.
   *
   * ⛔ THE SWAP OWNS ITS REASON (`confirmNote`), NEVER THE ADD FORM'S `note`.
   * Both reasons land in the append-only money journal, and DestinationList
   * measured the leak a shared field produces three times: a sentence typed for
   * one act was filed against another. Every act reads the box it was typed
   * into, and opening, moving or abandoning a confirm starts it empty.
   */
  const [confirmingId, setConfirmingId] = useState<number | null>(null);
  const [confirmNote, setConfirmNote] = useState('');
  const confirmNoteRef = useRef<HTMLInputElement>(null);
  /** Each row's `Remove`, so `Keep it` can hand the caret back to the control
   *  that asked (DestinationList's T1723 M4 finding: abandoning a confirm dropped
   *  focus to <body>). Keyed by `accountPriceId`, the row's identity. */
  const removeRefs = useRef(new Map<number, HTMLButtonElement>());
  /** Written ONLY by `Keep it`: a completed removal puts the caret on its notice. */
  const pendingRowFocus = useRef<number | null>(null);

  // Where the caret goes after an action. The control that was clicked is
  // `disabled` while the request is in flight and the browser blurs a disabled
  // element, so focus would drop to <body> on every action without this.
  //
  // ⚠️ KEYED ON THE SLOT TOO. A block message whose block leaves the screen (the
  // reload after a send can come back with no prices, see account-book-loader.ts)
  // moves to the form slot, which is a NEW element: without `bannerBlock` here
  // the old one would unmount under the caret and focus would drop to <body>.
  const blocks = groupByAccount(prices);
  const bannerBlock = notice !== null && notice.at !== 'form'
    && blocks.some((b) => b.accountId === notice.at)
    ? notice.at
    : null;
  useEffect(() => {
    if (notice === null) return;
    noticeRef.current?.focus();
  }, [notice, bannerBlock]);

  /**
   * ⭐ WHERE THE CARET GOES WHEN THE FORM CHANGES MODE (T651 M5 ux-review, two
   * BLOCK findings — both MEASURED on the served build, not inferred).
   *
   * 1. "Change price" APPEARED TO DO NOTHING. It swaps the form to edit mode —
   *    but the form sits BELOW the list, and this book is the longest of the
   *    three tabs: measured at 3,671px down a 3,647px list against a 720px
   *    viewport, i.e. about four screens. `window.scrollY` was identical before
   *    and after the click. Helen clicks, nothing visibly happens, and she
   *    cannot tell WHICH pair she is about to change the price of. On a money
   *    screen, mid-call, that is the hesitation this build exists to remove.
   * 2. "Stop editing" DROPPED FOCUS TO THE DOCUMENT BODY. The button removes itself
   *    from the DOM, so a keyboard user lost their place and had to tab from
   *    the top of the page again (WCAG 2.4.3).
   *
   * ⛔ FOCUS, NOT `scrollIntoView`, AND THAT IS THE POINT: moving focus scrolls
   * the control into view as a side effect AND tells a screen-reader user where
   * they now are. A bare scroll would fix only the sighted half.
   *
   * ⛔ THE RESIDUAL THIS NOTE RECORDED IS NOW HALF PAID (T1723 M3, II-admin-F10),
   * and it is edited rather than deleted so the ledger stays readable.
   * WHAT WAS RECORDED (T651 M5 code-review): `screens/admin/AccountList.tsx` and
   * `screens/admin/DestinationList.tsx` both had a "Stop editing" button that
   * unmounts itself with no focus-restore, AND neither scrolled its form into
   * view on Edit/Rename.
   * ⭐ PAID: the second half. Both siblings now move focus into the form on
   * `startEdit`, using the shape below, so the click is no longer silent.
   * ⚠️ STILL LIVE: the first half. "Stop editing" on both siblings still drops
   * focus to <body> when it removes itself; only this file restores it (the
   * `'cancel'` branch of the effect below).
   * ⚠️ Fixing them is cheaper than it looks — the shape below is copyable — and
   * the case for doing all three at once is that the price book is the LONGEST
   * list of the three, so it is where the defect bites hardest and where it was
   * found, not where it uniquely exists.
   *
   * ⚠️ GATED ON AN EXPLICIT REASON rather than on `editing` changing, because
   * `startAdd()` also runs after a successful save — where focus belongs on the
   * status message (the effect above), not back in the form. A single "did the
   * mode change" trigger is exactly how the confirmation gets stolen.
   */
  useEffect(() => {
    const why = pending.current;
    pending.current = null;
    if (why === 'edit') amountRef.current?.focus();
    else if (why === 'cancel') homeRef.current?.focus();
  }, [editing]);

  /**
   * ⭐ board 2705 — OPENING THE CONFIRM PUTS THE CARET IN ITS REASON, and
   * abandoning it (`Keep it`) gives the caret back to that row's `Remove`.
   * DestinationList.tsx's effect, for its reasons: the control that was clicked
   * unmounts in the swap, so without this focus drops to <body> both ways.
   */
  useEffect(() => {
    if (confirmingId === null) {
      const rowId = pendingRowFocus.current;
      pendingRowFocus.current = null;
      if (rowId !== null) removeRefs.current.get(rowId)?.focus();
      return;
    }
    confirmNoteRef.current?.focus();
  }, [confirmingId]);

  /** Open, move or close the confirm. ⛔ It always starts or ends EMPTY: a reason
   *  belongs to one removal, never to the next one (see `confirmNote`). */
  const setConfirming = (accountPriceId: number | null) => {
    setConfirmingId(accountPriceId);
    setConfirmNote('');
    setWhyRefused(false);
    setNotice(null);
  };

  /**
   * ⭐ board 2705 — THE REMOVAL'S OWN RUNNER, shaped like `sendToPortal` and NOT
   * `run`, for `sendToPortal`'s reason (`run`'s success arm is about a saved
   * figure). Its message renders in the BLOCK, at `at: block.accountId` - board
   * 2810's slot - never in the form, because the act happened in the block.
   *
   * ⛔ THE SUCCESS SENTENCE SAYS A SEND IS STILL NEEDED, and so do the confirm's
   * hint and the block's verdict line (which now reads "Prices have changed since
   * this was last sent."). A removal reaches the home only through "Send prices
   * to the portal", the same deliberate act as every other price change; no
   * sentence may claim the home has stopped seeing it before then.
   */
  const removeFromList = (targetAccountId: number, row: ApiAccountPrice) => {
    if (inFlight.current) return;
    const reason = confirmNote.trim();
    if (reason === '') {
      setNotice(null);
      setWhyRefused(true);
      confirmNoteRef.current?.focus();
      return;
    }
    setWhyRefused(false);
    inFlight.current = true;
    setBusy(true);
    setNotice(null);
    void (async () => {
      try {
        const result = await removeAccountPrice(row.accountId, row.destinationId, reason);
        if (!result.ok) {
          // The confirm stays open with her sentence: same pair, same act, so
          // the reason is still true of a retry (board 712's rule).
          setNotice({ kind: 'error', text: result.message, at: targetAccountId });
          return;
        }
        setConfirmingId(null);
        setConfirmNote('');
        setNotice({
          kind: 'ok',
          text: 'Price removed. Send prices to the portal so the home stops seeing it.',
          at: targetAccountId,
        });
        onChanged();
      } finally {
        inFlight.current = false;
        setBusy(false);
      }
    })();
  };

  const run = async (action: () => Promise<PriceResult>, okText: string) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setNotice(null);
    try {
      const result = await action();
      if (!result.ok) {
        setNotice({ kind: 'error', text: result.message, at: 'form' });
        return;
      }
      // ⭐ THE TWO REPORTED FACTS, SAID RATHER THAN SWALLOWED.
      //  · `changed === false` — the figure was already that, so nothing was
      //    written and nothing was journalled. Claiming a save that did not
      //    happen is how an operator stops trusting the screen.
      //  · `offerable === false` — the price IS stored and DOES resolve, but the
      //    home or the destination is out of use, so no picker will offer this
      //    pair for new work. The `searchable` precedent: report the measurable
      //    fact instead of letting her wait for a row that never appears.
      const extra = result.changed === false
        ? ' That was already the price, so nothing was changed.'
        : result.offerable
          ? ''
          : ' Note: that home or that destination is out of use, so this pair is not offered for new bookings until it is put back.';
      setNotice({ kind: 'ok', text: `${okText}${extra}`, at: 'form' });
      // The house invalidation: refetch everything the screen shows.
      onChanged();
    } finally {
      inFlight.current = false;
      setBusy(false);
      // ⛔⛔ A REASON IS BOUND TO A TARGET, AND IT IS CLEARED WHEN THE TARGET
      // CHANGES — NOT WHEN AN ATTEMPT ENDS (board 712).
      //
      // ⚠️ THIS IS A CORRECTION. It used to reset here, in `finally`, on every
      // outcome, justified by M4's confirmed cross-row leak on the sibling
      // destinations tab. That reasoning CONFLATED TWO RULES and only one of
      // them is load-bearing: what prevents M4's leak is resetting on every
      // TARGET SWITCH, which `startAdd` and `startEdit` already do. Resetting on
      // every OUTCOME is a strictly wider rule that buys nothing extra and
      // charges the operator a retyped sentence after every server error — and
      // on the success path it was redundant anyway, because onSave calls
      // startAdd before this ran. So it only ever did new work on FAILURE, which
      // is the one case where keeping her sentence is the kind thing.
      //
      // ⛔ BUT THE NARROW FIX IS NOT "JUST STOP RESETTING", AND THAT IS THE
      // SUBTLE PART. In ADD mode the two selects ARE the target chooser and they
      // are enabled, so: type a reason, submit, get a 409, then change the
      // Destination and submit again — and the preserved reason would be filed
      // against a DIFFERENT pair. That is M4's leak exactly, arriving through a
      // <select> instead of a row button. ⇒ The reset moved ONTO the target
      // changes (both selects, plus the two mode switches), which makes the
      // invariant complete and states it in one sentence instead of two.
      //
      // ⛔ THE JOURNAL IS STILL THE REASON IT MATTERS: account_price_audit is
      // append-only with no erasure path, so a reason attached to the wrong
      // price is permanent and reads as authoritative.
    }
  };

  /**
   * ⭐ T1704 — THE PROJECTION'S OWN RUNNER, AND IT IS DELIBERATELY NOT `run`.
   *
   * ⛔ `run` IS TYPED `() => Promise<PriceResult>`, whose success arm REQUIRES
   * `offerable: boolean` — so a projection result does not typecheck through it,
   * and widening the type to make it fit would be worse than the type error:
   * `run`'s success path appends "Note: that home or that destination is out of
   * use, so this pair is not offered for new bookings until it is put back."
   * whenever `offerable` is falsy. A successful send would arrive on screen
   * carrying a sentence about a price nobody edited.
   *
   * WHAT IS REUSED IS EXACTLY THE TWO THINGS THAT ARE ABOUT THE SCREEN AND NOT
   * ABOUT PRICES: the `inFlight` ref latch (a boolean fixed at render time
   * cannot stop two clicks in one task) and `setNotice`, which the focus effect
   * above already moves the caret to.
   */
  const sendToPortal = (targetAccountId: number) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    setNotice(null);
    void (async () => {
      try {
        const result = await projectPortalDestinations(targetAccountId);
        if (!result.ok) {
          setNotice({ kind: 'error', text: result.message, at: targetAccountId });
          return;
        }
        // ⚠️ `>= 0` ONLY. The receipt emits -1 for a count the portal did not
        // send, precisely so a render cannot mistake it for one; 0 IS a real
        // count and must still be said.
        const active = result.projection.activeForAccount;
        const listed = active >= 0
          ? ` This home can now be sent to ${String(active)} ${active === 1 ? 'destination' : 'destinations'}.`
          : '';
        // ⛔ THE SEND IS REPORTED EVEN WHEN THE RECORD IS NOT SAVED. The portal
        // was updated; claiming otherwise would send Helen to repeat an act that
        // already happened.
        const unsaved = result.recorded
          ? ''
          : ' The portal was updated, but this screen could not save a record of it, so the line below may look out of date.';
        setNotice({ kind: 'ok', text: `Prices sent to the portal.${listed}${unsaved}`, at: targetAccountId });
        // ⛔ WITHOUT THIS THE VERDICT LINE STAYS STALE UNTIL THE VIEW IS
        // RE-ENTERED: `meta` refreshes only through onChanged -> Admin.load ->
        // loadMeta. The one line this whole feature exists to render would be
        // wrong immediately after the act that made it right.
        onChanged();
      } finally {
        inFlight.current = false;
        setBusy(false);
      }
    })();
  };

  const startAdd = () => {
    setEditing(null);
    submitted.current = null;
    setAccountId(homeDefault);
    setDestinationId('');
    setAmount('');
    setNote('');
    setNotice(null);
    // board 2705: an open removal confirm is a half-finished act; it closes, and
    // its reason goes with it.
    setConfirmingId(null);
    setConfirmNote('');
  };

  const startEdit = (p: ApiAccountPrice) => {
    // The form is screens below the row that was clicked — see the focus effect.
    pending.current = 'edit';
    submitted.current = null;
    setEditing(p.accountPriceId);
    setAccountId(String(p.accountId));
    setDestinationId(String(p.destinationId));
    setAmount(p.priceCents == null ? '' : asEuro(p.priceCents));
    setNote('');
    setNotice(null);
    setConfirmingId(null);
    setConfirmNote('');
  };

  const onSave = () => {
    const account = Number(accountId);
    const destination = Number(destinationId);
    if (!Number.isSafeInteger(account) || account <= 0
      || !Number.isSafeInteger(destination) || destination <= 0) {
      setNotice({ kind: 'error', text: 'Choose both a home and a destination before saving.', at: 'form' });
      return;
    }
    // Answered before the round trip. The server's 400 stays authoritative.
    const cents = parseEuroToCents(amount);
    if (cents == null) {
      setNotice({
        kind: 'error',
        text: 'The price needs to be an amount above zero, in euro and cents - for example 55 or 55.50.',
        at: 'form',
      });
      return;
    }
    // ⛔ THE FIGURE IS PART OF WHAT THE REASON IS ABOUT, so a reason typed for a
    // previous attempt does not carry to a DIFFERENT figure. See `submitted`.
    // ⚠️ CHECKED AT SUBMIT, NOT ON EVERY KEYSTROKE — deliberately. Clearing as
    // she types would eat her sentence for a slip she immediately corrects; what
    // matters is whether the reason is still true of the change being MADE.
    const figureMoved = submitted.current !== null && amount.trim() !== submitted.current;
    if (figureMoved || note.trim() === '') {
      setNote('');
      setNotice({
        kind: 'error',
        text: figureMoved
          ? 'The price changed since you last tried, so say why again. The reason is kept with your name against this price.'
          : 'Say why, in a few words. It is kept with your name against this price.',
        at: 'form',
      });
      return;
    }
    const isEdit = editing != null;
    const reason = note.trim();
    // What this attempt is about, so a later change of figure invalidates it.
    submitted.current = amount.trim();
    void run(async () => {
      const result = isEdit
        ? await setAccountPrice(account, destination, cents, reason)
        : await createAccountPrice(account, destination, cents, reason);
      if (result.ok) startAdd();
      return result;
    }, isEdit ? 'Price saved.' : 'Price added.');
  };

  // ONE Banner element, rendered at exactly one slot (the slot is chosen above,
  // beside the focus effect that depends on it).
  const banner = notice && (
    <Banner kind={notice.kind} elementRef={noticeRef} tabIndex={-1}>
      {notice.text}
    </Banner>
  );

  return (
    <>
      {/* ⛔ ONLY SAFE WHEN THE EMPTINESS IS KNOWN — see the prop's own note.
          AccountBook carries the honest line when it is not (T698).
          ⭐ T1749 M8 (Captain's M7 review, rework 2) — the library's `EmptyState`
          (§7.1), the same move the Accounts and Destinations tabs already took;
          this tab was the last bare `<p class="helper-note">` empty state. */}
      {prices.length === 0 && !emptinessUnknown && (
        <EmptyState headline="No agreed prices captured yet. Add the first one in the form below." />
      )}

      {/* ⭐ ONE BLOCK PER ACCOUNT: the home's name, then what it pays to go
          where. Before this the book was one flat list of 45 rows repeating the
          home's name on every line, so reading "what does Kilcoole pay?" meant
          scanning for a name rather than finding a heading — and M4's projection
          into the portal could not be checked by eye at all.
          ⛔ THE BLOCK MARKUP IS STILL NursingHomes.tsx'S, PLUS ONE CLASS THIS
          SCREEN OWNS — and the extra class is a CORRECTION, not an ornament.
          The reuse was justified above on "this adds no CSS to keep in step";
          a ux-review then measured the served page and found the heading
          computed 20px/600/navy-700 and `.op-name` on every row computed
          20px/600/navy-700 too, so the boundary was invisible. `.op-review-home`
          styles a margin and nothing else. The same markup means something
          different in a dense context: the roster frames it in `.op-review`
          over 0-3 rows, this book runs 18-29. `.book-account-heading` carries
          the band, the rule and the stickiness, scoped HERE so the roster,
          which reads correctly, is not restyled by a screen it never sees.
          The full reasoning is on the rule in app.css. */}
      {blocks.map((block) => {
        // Computed once per block: the verdict is read twice below (the guard
        // and the text), and calling it twice would let a future edit change one
        // and not the other.
        const verdict = projectionVerdict(meta, pricesTruncated, block);
        /* ⛔⛔ T1749 M9 rework 2 (Captain's four-gate check) — THE PROVENANCE COLUMN
           IS WITHHELD WHEN NO ROW IN THIS BLOCK HAS ONE, and the reason is a
           measurement rather than a preference.

           MEASURED live on the seat at 1024x700, the three seeded account blocks:
           Kilcoole Lodge 27 rows / 0 flagged, Leopardstown 18 / 0, Seaview 1 / 1.
           `Where it came from` held a 205px head over 45 empty cells in two of the
           three blocks — M9's own `nowrap` fix is what widened that head — and 205
           of a 752px table is 27% of the width promising a fact that is not there.
           With the column withheld the head row reads `Destination=480 | Price=78 |
           Change=193` at the same 752px table width: the whole 205px goes to the
           destination name, which is the column that runs out of room first.

           ⛔ PER BLOCK, NEVER PER SCREEN. Seaview's one row DOES carry the chip, so
           its table keeps the column and its head still explains the chip under it.
           A screen-level test would hide a live value; a block-level one cannot.
           ⚠️ WHAT THE COLUMN HEAD WAS FOR IS PRESERVED WHERE IT IS EARNED: the M9
           note below says the head exists so a reader can learn what the grey chip
           in column 3 means. That argument only holds where a chip renders — which
           is exactly the condition this flag tests. */
        /* ⭐ board 2812 M5 (fix round 1, W1): on a home's page (`onlyAccountId`, this
           table's one live host, beside the homes column) the chip rides UNDER THE PRICE
           instead of in a column of its own: at 1024x700 the 205px nowrap head took the
           table to 725px in a 528px pane, and "Change price" and "Remove" sat past the
           screen's edge. The account book keeps the column. */
        const flagUnderPrice = onlyAccountId !== undefined;
        const showProvenance = !flagUnderPrice && block.rows.some((p) => p.edited);
        return (
        <div key={block.accountId} class="op-review-home">
          <h3 class="screen-sub screen-sub--item book-account-heading">{block.accountName}</h3>
          {/* ⭐ T1749 M9 (DS-24) — ONE `Table` PER ACCOUNT BLOCK, and the block was
              a table all along. `ul.op-list` of `li.op-row` was carrying a FOUR
              COLUMN GRID (`.op-review-home .op-row`) drawn on top of list
              semantics: the markup said "list", the CSS said "table", and the
              column heads a reader needed — which figure is the price, what the
              grey chip in column 3 means — existed nowhere. `Table` is the
              library's frame (§7.1), so the four heads are announced, `role=row`
              / `role=cell` replace the grid, and the retired
              `.op-review-home .op-row` rule went with `style-contract.spec.ts`'s
              GRIDS entry for it in this same commit.

              ⛔ THE FOUR CELL SUBTREES ARE UNCHANGED, DELIBERATELY. `.op-name`,
              `.op-price` / `.op-price--none`, `.op-row-flag` and the quiet button
              keep their exact markup and copy, so K3's money voice, the account
              heading differential (which pins that `.op-name` declares no
              background and no border) and S28(a)'s `data-fb-opaque` sweep all
              still grade the thing they were written against.

              ⛔ AND NO `cellClass` IS PASSED, which is a decision rather than an
              omission. `Table.cellClass` may only name a class the SURFACE sheet
              declares (`Table.tsx`'s guard), and this sheet's column classes
              (`.place-col`, `.band-col`) are every one of them scoped to
              `.book-table` / `.portal-table` — inert on `.data-table`. Passing an
              inert class is the defect this repo has found three times; the one
              thing the column genuinely needs — the price read on its last digit
              — is `align: 'end'`, which is choosing a variant rather than drawing
              one. The leftover width goes to the destination through ONE scoped
              rule in app.css, where the retired grid used to say it.

              ⚠️ The account name is the table's accessible NAME, not a cell: two
              tables on one screen with no names are two tables a screen-reader
              user cannot tell apart (`Table`'s own `label` note), and this screen
              renders one per home. It is the same string the `<h3>` above already
              shows, so it adds no fact to the page.
              ⛔ board 2812 M4 fix round (C6, security Low): an earlier line here said
              the click recorder never reads this name because it is not a control's.
              That was FALSE: `describeTarget` (`feedback/clicks.ts`) reads
              `aria-label` from ANY element a click lands on, and a tap on the table's
              own box (a cell's padding) resolves to the table. So `Table` carries
              `data-fb-opaque` itself, as `List`, `CountRow`, `TabStrip` and `SubRail`
              do, and the recorder writes the bare tag. */}
          {(() => {
            const table = (
            <Table
              label={`Agreed prices for ${block.accountName}`}
              columns={[
                { head: 'Destination' },
                { head: 'Price', align: 'end' },
                ...(showProvenance ? [{ head: 'Where it came from' }] : []),
                { head: 'Change' },
              ]}
              rows={block.rows.map((p) => ({
                key: String(p.accountPriceId),
                cells: [
                  /* ⛔ THE HOME'S NAME IS NOT REPEATED ON THE ROW — the heading above
                     carries it now, and a name printed twice on every line is exactly
                     the clutter the owners bounced off a previous tool for. The row
                     is the destination and the figure. */
                  <span class="op-name">{p.destinationName}</span>,
                  /* ⛔ `== null`, NEVER a falsy check. Zero is a price.
                     ⭐ T1723 M3 (K3): the figure is the thing Helen came to read, and
                     it used to be the quietest object on its own row. `.op-price` is
                     24px navy tabular, in the column the head now names (DESIGN.md
                     §3.2, "numbers Helen acts on"), rendered by the SHARED `euro()`.
                     The empty state keeps the caption weight through the `--none`
                     modifier: "Not priced yet" is an absence, not a figure. */
                  <>
                    {p.priceCents == null
                      ? <span class="op-price op-price--none">Not priced yet</span>
                      : <span class="op-price">{euro(p.priceCents)}</span>}
                    {/* (W1) the price cell is at its min-content width, so the chip
                        stacks under the figure rather than beside it. */}
                    {flagUnderPrice && p.edited && (
                      <span class="op-row-flag"><span class="chip chip--quiet">Set here, not from the sheet</span></span>
                    )}
                  </>,
                  /* ⛔ THE PROVENANCE SLOT IS RENDERED ON EVERY ROW OF A BLOCK THAT HAS
                     ONE, EMPTY OR NOT — a head with nothing under it on SOME rows is
                     the same honesty the chip itself is about, and the cells must line
                     up under the head. ⛔ T1749 M9 rework 2: what changed is the
                     BLOCK-level test above, not this one. A block where no row is
                     flagged renders neither the head nor these cells, because a head
                     with nothing under it on EVERY row is a promise, not honesty. */
                  ...(showProvenance ? [<span class="op-row-flag">
                    {/* ⛔ THE LABEL SAYS WHAT THE FLAG MEANS, AND "Changed here" DID NOT
                        (T651 M5 ux-review). `edited` means THE OPERATOR OWNS THIS FIGURE
                        AND THE SHEET DOES NOT — which is true of a price she ADDED as
                        well as one she changed, so the chip appeared on rows that had
                        never been changed and read as a warning about nothing. What it
                        actually tells her is that a re-seed will not touch this row. */}
                    {p.edited && <span class="chip chip--quiet">Set here, not from the sheet</span>}
                  </span>] : []),
                  /* ⛔ THE BUTTON IS NOT NAMED AFTER THE ROW, the same two rules
                     AccountList.tsx and DestinationList.tsx both state: the feedback
                     widget's click recorder reads a control's accessible name into a
                     stored trail and its sanitiser "catches emails and 7+ digit runs,
                     never names"; and a row button called `Edit <name>` would let
                     somebody create a destination called "Continue" and change what a
                     Playwright accessible-name matcher resolves to on another screen.
                     The row's identity is carried by the row context instead.
                     ⭐ board 2705: `Remove` follows the same two rules, and its
                     confirm swaps THIS cell in place (DestinationList's K2 shape). */
                  confirmingId === p.accountPriceId ? (
                    <span class="op-row-confirm">
                      {/* A VISIBLE label, not a placeholder (DestinationList's
                          A2-pricelists-1 note): it survives typing, and it is the
                          input's only accessible name. The id carries the row. */}
                      <label class="field-label" for={`price-remove-note-${String(p.accountPriceId)}`}>
                        Why is it coming off this home's list?
                      </label>
                      <input
                        id={`price-remove-note-${String(p.accountPriceId)}`}
                        class="login-input op-row-why"
                        type="text"
                        ref={confirmNoteRef}
                        data-fb-opaque
                        autocomplete="off"
                        {...(whyRefused ? { 'aria-invalid': 'true', 'aria-describedby': PRICE_REMOVE_WHY_ERROR_ID } : {})}
                        value={confirmNote}
                        onInput={(e) => {
                          setWhyRefused(false);
                          setConfirmNote((e.target as HTMLInputElement).value);
                        }}
                      />
                      {/* ⭐ (W5) the refusal, on its own line under the field
                          (`.op-row-confirm .field-error`, flex-basis 100%). */}
                      {whyRefused && (
                        <span class="field-error" id={PRICE_REMOVE_WHY_ERROR_ID} role="alert">
                          {PRICE_REMOVE_WHY_WORDS}
                        </span>
                      )}
                      {/* ⛔ "Do not name a resident" (security review, Article 9): this
                          reason is journalled for ever in an append-only table with no
                          erasure path, and the question invites resident detail. */}
                      <span class="field-hint">
                        The home keeps seeing it until you send prices to the portal. Do not name a resident.
                      </span>
                      <button
                        type="button"
                        class="btn btn--quiet btn--destructive"
                        data-fb-opaque
                        disabled={busy}
                        onClick={() => removeFromList(block.accountId, p)}
                      >
                        Yes, remove this price
                      </button>
                      <button
                        type="button"
                        class="btn btn--quiet"
                        data-fb-opaque
                        disabled={busy}
                        onClick={() => {
                          // The one writer of `pendingRowFocus`: abandoning the
                          // question hands the caret back to this row's Remove.
                          pendingRowFocus.current = p.accountPriceId;
                          setConfirming(null);
                        }}
                      >
                        Keep it
                      </button>
                    </span>
                  ) : (
                    /* ⭐ ONE LINE AT REST, through the existing flex wrapper the
                       block's Send already wears (`.admin-add-actions`, app.css).
                       Bare, the two buttons stacked: the destination cell takes the
                       row's width, the action cell got min-content, and every price
                       row grew to 97px (live drive at 1024x768, board 2705). No
                       stylesheet is touched. The confirm keeps `.op-row-confirm`. */
                    <span class="admin-add-actions">
                      <button
                        type="button"
                        class="btn btn--quiet"
                        data-fb-opaque
                        disabled={busy}
                        onClick={() => startEdit(p)}
                      >
                        Change price
                      </button>
                      {/* ⛔ RAW `btn`, NOT THE LIBRARY `Button`: `Button` has no
                          pass-through for `data-fb-opaque` (DestinationList's
                          reason for the same choice), and every control here
                          carries it. */}
                      <button
                        type="button"
                        class="btn btn--quiet btn--destructive"
                        data-fb-opaque
                        ref={(el) => {
                          if (el === null) removeRefs.current.delete(p.accountPriceId);
                          else removeRefs.current.set(p.accountPriceId, el);
                        }}
                        disabled={busy}
                        onClick={() => {
                          // The row being edited is about to leave the list: the
                          // form goes back to adding, so it cannot save it after.
                          if (editing === p.accountPriceId) startAdd();
                          setConfirming(p.accountPriceId);
                        }}
                      >
                        Remove
                      </button>
                    </span>
                  ),
                ],
              }))}
            />
            );
            /* ⭐ board 2812 M4 (measured on the M4 seat at 1024x700): on a home's page the
               table sits beside the homes column, ~528px wide, and an edited row's
               provenance column with a Remove confirm open took it to 752px, so the whole
               pane scrolled sideways. There it is the library's `TableScroll` region: the
               TABLE scrolls, never the page. Its label names no home (a tap on the region
               is recorded by its name, and the table's own name carries the home). The
               account book (full width) is unchanged. */
            return onlyAccountId === undefined
              ? table
              : <TableScroll label="Agreed prices, scrolls sideways">{table}</TableScroll>;
          })()}

          {/* ⭐ T1704 — THE OPERATOR TRIGGER, AND IT SITS INSIDE THE BLOCK ON
              PURPOSE. The account it sends is the block it is rendered in, so
              the scope is a property of WHERE THE CONTROL LIVES rather than of a
              lookup that could pick the wrong home. Nothing here searches
              `prices`; `block` is the group this render is already inside.
              ⛔ NOT A RAIL ITEM AND NOT A SCREEN-LEVEL BUTTON: a single "send"
              at the top of the book would need to be told WHICH home, which is
              the question the block already answers.
              ⛔ `data-fb-opaque` LIKE EVERY OTHER CONTROL HERE, and the label
              names no customer and no destination — the click recorder stores a
              control's accessible name, and its sanitiser catches emails and
              long digit runs, never names.
              ⭐ board 2812 M5 step 0 (e): `.book-foot`, the existing foot-of-a-book spacing
              rule (RateList's add toggle wears it), so the send sits a step under the table
              instead of touching it (M4's ux-review N3). */}
          <div class="book-foot">
            <button
              type="button"
              class="btn btn--quiet"
              data-fb-opaque
              disabled={busy}
              onClick={() => sendToPortal(block.accountId)}
            >
              Send prices to the portal
            </button>
          </div>
          {bannerBlock === block.accountId && banner}
          {verdict !== null && <p class="helper-note">{verdict}</p>}
        </div>
        );
      })}

      {/* ⭐ board 2705 — A REAL SEPARATOR ELEMENT (DESIGN.md §5.1 "destructive
          never adjacent to primary"), DestinationList's K2 fix for the same
          shape: the last block's red `Remove` would otherwise sit directly above
          the screen's one gold `Add price`. Measured by conformance.spec.tsx's
          adjacency arm, which reds without it. The <h3> inside `.op-add` is
          inside the block, so it separates nothing. */}
      <hr class="section-rule" />
      <div class="op-add">
        <h3 class="screen-sub">{editing == null ? 'Add a price' : 'Change this price'}</h3>

        {/* ⛔ BOTH KEYS ARE CHOSEN, NEVER TYPED. A price is written against an
            account id and a destination id; a name typed into a box is how one
            home ends up with two price books. The two catalogues arrive as
            props for exactly this — see the prop contract M2 fixed.
            ⚠️ A <select> is the one control type whose textContent is every
            option concatenated, which is why the marker on these two is the
            least inert on the screen. */}
        <label class="field-label" for="price-account">Home</label>
        <select
          id="price-account"
          class="login-input"
          ref={homeRef}
          data-fb-opaque
          disabled={editing != null}
          value={accountId}
          onChange={(e) => {
            // ⛔ THE TARGET CHANGED, so the reason no longer belongs to it.
            setNote('');
            submitted.current = null;
            const next = (e.target as HTMLSelectElement).value;
            setAccountId(next);
            // board 2705: a destination the NEW home already has is no longer
            // offered below, so a pick of it is cleared rather than left
            // selected in a list that does not show it.
            if (destinationId !== '' && heldDestinationIds(blocks, next).has(Number(destinationId))) {
              setDestinationId('');
            }
          }}
        >
          <option value="">Choose a home</option>
          {accounts.map((a) => (
            <option key={a.accountId} value={String(a.accountId)}>{a.name}</option>
          ))}
        </select>

        <label class="field-label" for="price-destination">Destination</label>
        <select
          id="price-destination"
          class="login-input"
          data-fb-opaque
          disabled={editing != null}
          value={destinationId}
          onChange={(e) => {
            // ⛔ THE TARGET CHANGED, so the reason no longer belongs to it.
            setNote('');
            submitted.current = null;
            setDestinationId((e.target as HTMLSelectElement).value);
          }}
        >
          <option value="">Choose a destination</option>
          {/* ⭐ board 2705 — ADD MODE ONLY, the places the chosen home does not
              already have (`heldDestinationIds`, ids only). In EDIT mode the
              select is disabled and must still show the pair being changed, so
              the full catalogue renders as before. */}
          {(editing == null
            ? destinations.filter((d) => !heldDestinationIds(blocks, accountId).has(d.destinationId))
            : destinations
          ).map((d) => (
            <option key={d.destinationId} value={String(d.destinationId)}>{d.name}</option>
          ))}
        </select>

        <label class="field-label" for="price-amount">Price in euro</label>
        <input
          id="price-amount"
          class="login-input login-input--euro"
          ref={amountRef}
          type="text"
          inputMode="decimal"
          data-fb-opaque
          autocomplete="off"
          value={amount}
          onInput={(e) => setAmount((e.target as HTMLInputElement).value)}
        />

        <label class="field-label" for="price-note">Why</label>
        <input
          id="price-note"
          class="login-input"
          type="text"
          data-fb-opaque
          autocomplete="off"
          value={note}
          onInput={(e) => setNote((e.target as HTMLInputElement).value)}
        />
        {/* ⭐ T1749 M7 rework 1 (Q139) — `.field-hint`, not `.helper-note`. It sits
            under a field and says what shape the answer takes, which is what a
            hint is; spelled `.helper-note` it rendered at BODY size with no top
            margin beside caption-sized siblings, and the Q139 census, which counts
            `.field-hint`, could not see it at all. Class only: these screens'
            hint WORDS are graded at M9, when they adopt `FormField`. */}
        <p class="field-hint">
          A few words, kept with your name against this price. It cannot be edited or removed
          afterwards, so it is worth saying what was agreed and with whom.
        </p>

        {bannerBlock === null && banner}

        <div class="admin-add-actions">
          {/* ⭐ board 2812 M4 fix round (L1): at REST "Add price" is QUIET, not gold. M3's
              rule is that a screen's gold is its NEXT STEP, and a home's Prices tab has
              none at rest (measured at 1024x700 on the seat, this gold sat at 1219px,
              far below the plane). "Save this price", once Helen has asked to change a
              price, is her next step, so it stays gold. */}
          <button
            type="button"
            class={editing == null ? 'btn btn--quiet' : 'btn btn--primary'}
            data-fb-opaque
            disabled={busy}
            onClick={onSave}
          >
            {editing == null ? 'Add price' : 'Save this price'}
          </button>
          {editing != null && (
            <button
              type="button"
              class="btn btn--quiet"
              data-fb-opaque
              disabled={busy}
              onClick={() => { pending.current = 'cancel'; startAdd(); }}
            >
              Stop editing
            </button>
          )}
        </div>
      </div>
    </>
  );
}
